import { isInStock, isTakenOut } from "@/lib/partStatus";

/**
 * FIX-276: the value of one spare part = estimated_value × company resale
 * fraction, rounded to cents. Returns null when the part has no
 * estimated_value. The part page ("Value €") and Inventory both use this, so
 * they always agree to the cent.
 */
export function partValue(estimatedValue: number | string | null | undefined, resalePct: number): number | null {
  if (estimatedValue == null || estimatedValue === "") return null;
  const n = Number(estimatedValue);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * resalePct * 100) / 100;
}

interface ValuedPart {
  estimated_value: number | string | null;
  status: string | null;
  harvested_at?: string | null;
}

/** Sum of rounded part values (a part without a value counts as €0). Null when no part has a value. */
export function sumPartValues(parts: ValuedPart[], resalePct: number): number | null {
  let cents = 0;
  let any = false;
  for (const p of parts) {
    const v = partValue(p.estimated_value, resalePct);
    if (v == null) continue;
    any = true;
    cents += Math.round(v * 100);
  }
  return any ? cents / 100 : null;
}

/** Total value of parts in stock. */
export function stockValue(parts: ValuedPart[], resalePct: number): number {
  return sumPartValues(parts.filter((p) => isInStock(p.status)), resalePct) ?? 0;
}

/**
 * Total value of parts harvested in the calendar month of `now` (local time),
 * in stock or taken out since. Damaged parts don't count.
 */
export function harvestedThisMonthValue(parts: ValuedPart[], resalePct: number, now: Date = new Date()): number {
  const y = now.getFullYear();
  const m = now.getMonth();
  return (
    sumPartValues(
      parts.filter((p) => {
        if (!(isInStock(p.status) || isTakenOut(p.status)) || !p.harvested_at) return false;
        const d = new Date(p.harvested_at);
        return d.getFullYear() === y && d.getMonth() === m;
      }),
      resalePct,
    ) ?? 0
  );
}

export function formatEuro(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale || "en", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
